import * as _ from "lodash";
var fp = require('lodash/fp');

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds. The throttled
 * function comes with a cancel method to cancel delayed invocations and a flush method to immediately invoke
 * them. Provide an options object to indicate that func should be invoked on the leading and/or trailing edge
 * of the wait timeout. Subsequent calls to the throttled function return the result of the last func call.
 *
 * Note: If leading and trailing options are true, func is invoked on the trailing edge of the timeout only if
 * the the throttled function is invoked more than once during the wait timeout.
 *
 * @param func The function to throttle.
 * @param wait The number of milliseconds to throttle invocations to.
 * @param options The options object.
 * @param options.leading Specify invoking on the leading edge of the timeout.
 * @param options.trailing Specify invoking on the trailing edge of the timeout.
 * @return Returns the new throttled function.
 */
function throttle<T extends (...args: any[]) => any>(
    func: T,
    wait?: number,
    options?: _.ThrottleSettings
): T & _.Cancelable {
    return _.throttle(func, wait, options);
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since
 * the last time the debounced function was invoked. The debounced function comes with a cancel method to
 * cancel delayed invocations and a flush method to immediately invoke them. Provide an options object to
 * indicate that func should be invoked on the leading and/or trailing edge of the wait timeout. Subsequent
 * calls to the debounced function return the result of the last func invocation.
 *
 * Note: If leading and trailing options are true, func is invoked on the trailing edge of the timeout only
 * if the the debounced function is invoked more than once during the wait timeout.
 *
 * See David Corbacho’s article for details over the differences between _.debounce and _.throttle.
 *
 * @param func The function to debounce.
 * @param wait The number of milliseconds to delay.
 * @param options The options object.
 * @param options.leading Specify invoking on the leading edge of the timeout.
 * @param options.maxWait The maximum time func is allowed to be delayed before it’s invoked.
 * @param options.trailing Specify invoking on the trailing edge of the timeout.
 * @return Returns the new debounced function.
 */
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait?: number,
    options?: _.DebounceSettings
): T & _.Cancelable {
    return _.debounce(func, wait, options);
}

/**
 * @see _.times
 */
function times(n: number): number[];

/**
 * Invokes the iteratee function n times, returning an array of the results of each invocation. The iteratee
 * is invoked with one argument; (index).
 *
 * @param n The number of times to invoke iteratee.
 * @param iteratee The function invoked per iteration.
 * @return Returns the array of results.
 */
function times<TResult>(
    n: number,
    iteratee: (num: number) => TResult
): TResult[];

function times(
    n,
    iteratee?
) {
    if (iteratee !== undefined) {
        return _.times(n, iteratee);
    } else {
        return _.times(n);
    }
}

type Pipe<T, U> = (collection: T[]) => U[];
type PipeAny = Pipe<any, any>;
function fpFlow<T, U>(...args: PipeAny[]): Pipe<T, U>;
function fpFlow<T, U>(operations: PipeAny[]): Pipe<T, U>;
function fpFlow(...args) {
    return fp.flow(...args);
}

/**
 * Creates a new lodash object that inherits from the context object provided.
 * @param context The object to inherit from.
 * @return Returns the new lodash object.
 * @example
 * const lodash = FunctionUtil.runInContext();
 * console.log((lodash([1, 2, 3]).map(square).value()))
 * // => [1, 4, 9]
 */
function runInContext(context?: Object): _.LoDashStatic {
    return _.runInContext(context);
}

/**
 * Creates a function that invokes iteratees with the arguments provided to the created function and returns
 * their results.
 *
 * @param iteratees The iteratees to invoke.
 * @return Returns the new function.
 */
function over<TResult>(...iteratees: Array<_.Many<(...args: any[]) => TResult>>): (...args: any[]) => TResult[] {
    return _.over(...iteratees);
}

export const FunctionUtil = {
    throttle, debounce, times, fpFlow, runInContext, over
}

export default FunctionUtil;